import { useRef, useState, useEffect } from "react";
import { Upload, Link2, X, Loader2, Images } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function MediaUpload({ value, onChange, kind = "image", label, accept = "image/*", testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("upload");
  const [lib, setLib] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openLibrary = async () => {
    setMode("library");
    try { const { data } = await api.get("/admin/media"); setLib(data); } catch { setLib([]); }
  };

  const isVideo = kind === "video" || accept.includes("video");
  const preview = value ? resolveImg(value) : null;
  const btn = (m, active) => `text-xs px-2 py-1 rounded ${active ? "bg-[#DCA846] text-black" : "bg-zinc-800 text-zinc-300"}`;

  return (
    <div data-testid={testid} className="space-y-2">
      {label && <label className="text-xs uppercase tracking-wider text-zinc-400">{label}</label>}
      <div className="flex gap-2">
        <button type="button" onClick={() => setMode("upload")} className={btn("upload", mode === "upload")}><Upload className="inline h-3 w-3 mr-1" />Upload</button>
        {!isVideo && <button type="button" data-testid="media-library-btn" onClick={openLibrary} className={btn("library", mode === "library")}><Images className="inline h-3 w-3 mr-1" />Library</button>}
        <button type="button" onClick={() => setMode("url")} className={btn("url", mode === "url")}><Link2 className="inline h-3 w-3 mr-1" />URL</button>
      </div>

      {mode === "upload" && (
        <div>
          <input ref={inputRef} type="file" accept={accept} onChange={handleFile} className="hidden" />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="w-full rounded border border-dashed border-white/20 bg-zinc-900/50 py-6 text-sm text-zinc-400 hover:border-[#DCA846]/50 transition-colors">
            {uploading ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Click to upload file"}
          </button>
        </div>
      )}
      {mode === "library" && (
        <div data-testid="media-library-grid" className="max-h-56 overflow-y-auto rounded border border-white/10 bg-zinc-900/50 p-2">
          {lib === null ? <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-[#DCA846]" /> :
            lib.length === 0 ? <p className="py-6 text-center text-xs text-zinc-500">No uploads yet.</p> :
            <div className="grid grid-cols-4 gap-2">
              {lib.map((f) => (
                <button key={f.id} type="button" onClick={() => onChange(f.storage_path)}
                  className={`aspect-square overflow-hidden rounded border ${value === f.storage_path ? "border-[#DCA846]" : "border-white/10"} hover:border-[#DCA846]/60`}>
                  <img src={resolveImg(f.storage_path)} alt={f.alt_text || ""} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>}
        </div>
      )}
      {mode === "url" && (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="https://... (YouTube, Vimeo, direct video, or image URL)" className="bg-zinc-900 border-white/10 text-white" />
      )}

      {preview && (
        <div className="relative mt-2 inline-block">
          {isVideo && !String(value).match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
            <div className="rounded border border-white/10 bg-zinc-900 px-3 py-2 text-xs text-emerald-400 max-w-xs truncate">Video attached: {String(value).slice(0, 40)}</div>
          ) : (
            <img src={preview} alt="preview" className="h-24 w-auto rounded border border-white/10 object-cover" />
          )}
          <button type="button" onClick={() => onChange(null)} className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white"><X className="h-3 w-3" /></button>
        </div>
      )}
    </div>
  );
}
